/**
 * Whose clock the page is speaking in.
 *
 * The meeting happens in Brisbane and the API thinks in Brisbane, because Queensland is
 * fixed UTC+10 and every conversion on the server is arithmetic because of it. That is
 * correct for Brisbane and wrong for everybody else, and this page is served to
 * everybody else. Sydney and Melbourne read the same clock as us for half the year and
 * an hour out for the other half, Adelaide is thirty minutes off all year, and Perth is
 * two hours behind. None of that can be done with a fixed offset, so all of it is done
 * with Intl against real ICU rules.
 *
 * WHY THE PAGE NEEDS THE INSTANT. Availability used to come back as "09:30" and nothing
 * else. A wall-clock string cannot be converted, because it does not say what moment it
 * means. Every slot now carries `starts_at` as an epoch second and that is what
 * everything below reads; `start` is still on the wire and is still Brisbane's answer.
 *
 * WHY THE DAYS ARE REGROUPED. The API groups slots by the Brisbane date. Once times are
 * rendered somewhere else the day boundaries move with them, so a 9am Brisbane slot is
 * the previous evening in London and a late slot can cross midnight going the other way.
 * Grouping by the Brisbane date and labelling it with the visitor's would put slots
 * under the wrong heading, which is a worse lie than not converting at all.
 */

export const BRISBANE = 'Australia/Brisbane';

/**
 * The visitor's zone, or Brisbane when the browser will not say.
 *
 * Wrapped in a try because this is read on first render and an old browser throwing
 * here would take the whole page down rather than costing one label.
 */
export function detectTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || BRISBANE;
  } catch {
    return BRISBANE;
  }
}

/** "Australia/Adelaide" reads as Adelaide. The last segment is the part that names a place. */
export function zoneLabel(tz: string): string {
  const last = tz.split('/').pop() ?? tz;
  return last.replace(/_/g, ' ');
}

function parts(epochSec: number, tz: string, opts: Intl.DateTimeFormatOptions): Record<string, string> {
  const out: Record<string, string> = {};
  for (const part of new Intl.DateTimeFormat('en-AU', { timeZone: tz, ...opts })
    .formatToParts(new Date(epochSec * 1000))) {
    out[part.type] = part.value;
  }
  return out;
}

/** YYYY-MM-DD in the given zone. What the day strip groups on. */
export function localDateKey(epochSec: number, tz: string): string {
  const p = parts(epochSec, tz, { year: 'numeric', month: '2-digit', day: '2-digit' });
  return `${p.year}-${p.month}-${p.day}`;
}

/** "9:30am" in the given zone. */
export function formatTime(epochSec: number, tz: string): string {
  const p = parts(epochSec, tz, { hour: 'numeric', minute: '2-digit', hour12: true });
  return `${p.hour}:${p.minute}${(p.dayPeriod ?? '').toLowerCase().replace(/[.\s]/g, '')}`;
}

/** "Wednesday 12 August, 9:30am" in the given zone. The phrasing the emails use. */
export function formatLong(epochSec: number, tz: string): string {
  const p = parts(epochSec, tz, {
    weekday: 'long', day: 'numeric', month: 'long', hour: 'numeric', minute: '2-digit', hour12: true,
  });
  const meridiem = (p.dayPeriod ?? '').toLowerCase().replace(/[.\s]/g, '');
  return `${p.weekday} ${p.day} ${p.month}, ${p.hour}:${p.minute}${meridiem}`;
}

/** The three stacked lines on a day tile, plus the spoken version. */
export function dayLabel(epochSec: number, tz: string): {
  weekday: string; day: string; month: string; full: string;
} {
  const short = parts(epochSec, tz, { weekday: 'short', day: 'numeric', month: 'short' });
  const long = parts(epochSec, tz, { weekday: 'long', day: 'numeric', month: 'long' });
  return {
    weekday: short.weekday,
    day: short.day,
    month: short.month,
    // The tile splits its date across three elements, and the name a browser derives
    // from that is not how anybody says a date out loud. The explicit label wins.
    full: `${long.weekday} ${long.day} ${long.month}`,
  };
}

/**
 * True when a zone shows the same clock as Brisbane for this moment.
 *
 * Compared by rendered string rather than by offset arithmetic, so Sydney in July counts
 * as the same and Sydney in January does not, with no DST table anywhere in this file.
 */
export function readsAsBrisbane(epochSec: number, tz: string): boolean {
  return tz === BRISBANE || formatLong(epochSec, tz) === formatLong(epochSec, BRISBANE);
}
